import { read, newKey, frozenWrite } from '../../teams-schedules/models/api';

export const api = {
  state: () => read('/attendance/me/state'), settings: () => read('/attendance/settings'),
  tracking: () => read('/attendance/tracking'), live: () => read('/attendance/live'),
  reviews: status => read('/attendance/me/review-items', { status }),
  categories: () => read('/attendance/explanation-categories'),
  breakTypes: () => read('/attendance/break-types'),
  ownBreakTypes: () => read('/attendance/me/break-types'),
  queue: filters => read('/attendance/review-items', filters),
  review: id => read(`/attendance/review-items/${id}`),
  reviewSummary: () => read('/attendance/review-summary'),
  waivers: () => read('/attendance/waiver-categories'),
  corrections: range => read('/attendance/me/corrections', range),
  days: range => read('/attendance/me/days', range),
  month: month => read('/attendance/me/month', { month }),
  locks: year => read('/attendance/locks', { year }),
  lock: month => read(`/attendance/locks/${month}`),
  reportDay: params => read('/attendance/reports/day', params),
  reportPerson: params => read('/attendance/reports/person', params),
  reportTeam: params => read('/attendance/reports/team', params),
  reportTeams: () => read('/attendance/reports/teams'), reportPeople: () => read('/attendance/reports/people'),
  reportExport: id => read(`/attendance/reports/exports/${id}`),
};
export const paths = {
  start: '/attendance/me/session/start', end: '/attendance/me/session/end',
  breakStart: '/attendance/me/break/start', resume: '/attendance/me/break/end',
  pin: '/attendance/me/pin', settings: '/attendance/settings',
  tracking: id => `/attendance/tracking/${id}`, resetPin: id => `/attendance/tracking/${id}/pin-reset`,
  explain: id => `/attendance/me/review-items/${id}/explanation`,
  category: id => `/attendance/explanation-categories${id ? `/${id}` : ''}`,
  decide: id => `/attendance/review-items/${id ? `${id}/` : ''}decide`,
  waiver: id => `/attendance/waiver-categories${id ? `/${id}` : ''}`,
  correction: '/attendance/me/corrections',
  withdraw: id => `/attendance/me/corrections/${id}/withdraw`,
  lock: month => `/attendance/locks/${month}/lock`,
  unlock: month => `/attendance/locks/${month}/unlock`,
  reportExport: '/attendance/reports/export',
  reportExportFile: id => `/attendance/reports/exports/${id}/file`,
};
export const labels = { off_shift: 'Not in a session', available: 'Available', on_break: 'On break', idle: 'Idle', disconnected: 'Disconnected' };
export const gateActive = state => Boolean(state?.enabled && state?.tracked && state.next_action !== 'none');
export const inSession = state => Boolean(state?.enabled && state?.tracked && state.session && !state.session.ended_at);
export const needsExplanation = (before, after) => Boolean(after?.enabled && after?.tracked && ['idle', 'disconnected'].includes(before?.state) && after.state === 'available');
export const errorCopy = {
  ATTENDANCE_PERMISSION_REQUIRED: 'You do not have permission for this attendance action.',
  ATTENDANCE_PIN_INVALID: 'Incorrect PIN. Attempts are limited.',
  ATTENDANCE_PIN_RATE_LIMITED: 'Your PIN is locked for a while. Try again in 15 minutes.',
  ATTENDANCE_REVIEW_NOT_FOUND: 'This attendance item is no longer available.',
  ATTENDANCE_CATEGORY_NOT_FOUND: 'This explanation category is no longer available.',
  ATTENDANCE_INPUT_INVALID: 'Check the attendance values and try again.',
  ATTENDANCE_SETTING_BELOW_MINIMUM: 'This value is below the allowed minimum.',
  ATTENDANCE_IDEMPOTENCY_REQUIRED: 'This request needs a reference. Please refresh.',
  ATTENDANCE_USER_INVALID: 'This person is no longer available.',
  ATTENDANCE_REASON_REQUIRED: 'Please add a reason.',
  ATTENDANCE_BREAK_TYPE_INVALID: 'Choose an active break type.',
  ATTENDANCE_EXPLANATION_INVALID: 'Choose a category and add its required note.',
  ATTENDANCE_PIN_FORMAT: 'Use a PIN of 4–6 digits.',
  ATTENDANCE_HEARTBEAT_INVALID: 'Attendance connection could not be updated.',
  ATTENDANCE_AGENT_UNAVAILABLE: "This person isn't available right now.",
  SCHEDULE_INPUT_INVALID: 'Check the selected person and values.',
  ATTENDANCE_VERSION_CONFLICT: 'This item changed. Reloaded the latest.',
  ATTENDANCE_SELF_REVIEW: 'Your own item must be reviewed by someone else.',
  ATTENDANCE_OUTCOME_INVALID: 'This outcome is not allowed for this item.',
  ATTENDANCE_NOTE_REQUIRED: 'Add the required note before saving.',
  ATTENDANCE_MONTH_LOCKED: 'This month is locked. Ask an authorized reviewer to unlock it.',
  ATTENDANCE_LOCK_OPEN_ITEMS: 'This month has open items or pending corrections.',
  ATTENDANCE_LOCK_MONTH_INVALID: 'Only completed months can be locked.',
  ATTENDANCE_CORRECTION_PENDING: 'A correction for this time is already pending.',
  ATTENDANCE_CORRECTION_INVALID: 'Check the proposed correction time and reason.',
  ATTENDANCE_CORRECTION_NOT_FOUND: 'This correction is no longer available.',
  ATTENDANCE_RANGE_TOO_LARGE: 'Choose up to 92 days.',
  ATTENDANCE_TEAM_NOT_FOUND: 'This team is no longer available.',
  ATTENDANCE_USER_NOT_FOUND: 'This person is no longer available.',
  ATTENDANCE_EXPORT_NOT_FOUND: 'This export is no longer available.',
  ATTENDANCE_EXPORT_NOT_READY: 'This export is not ready yet.',
  ATTENDANCE_EXPORT_EXPIRED: 'This export has expired. Create a new one.',
  ATTENDANCE_EXPORT_FORMAT_UNAVAILABLE: "Excel export isn't available yet. Use CSV or PDF.",
  ATTENDANCE_EXPORT_FAILED: 'This export failed. Create a new one.',
  ATTENDANCE_IDEMPOTENCY_CONFLICT: 'This request reference already belongs to another action. Please refresh.',
  ATTENDANCE_NOT_TRACKED: 'Attendance tracking is off for you.',
  ATTENDANCE_NO_TEAM: 'Ask an admin to add you to a team.',
  ATTENDANCE_EARLY_LEAVE_CONFIRM: 'Your shift has not ended yet. Confirm that you want to finish early.',
  ATTENDANCE_STATE_INVALID: 'Your attendance state changed. Please refresh.',
  ATTENDANCE_BREAK_WINDOW_INVALID: 'This break is outside its allowed window.',
  ATTENDANCE_EXPLANATION_REQUIRED: 'Explain your automatically closed session before starting work.',
  ATTENDANCE_PIN_SETUP_REQUIRED: 'Set your PIN in My attendance before resuming.',
  ATTENDANCE_REVIEW_DECIDED: 'This item has already been reviewed and cannot be edited.',
  ATTENDANCE_RESOURCE_CONFLICT: 'This record changed. Please refresh.',
  ATTENDANCE_SCHEMA_NOT_READY: 'Attendance is temporarily unavailable. You can continue working.',
  ATTENDANCE_REQUEST_FAILED: 'Attendance could not be updated. Please retry the same request.',
};
export const errorText = error => error?.code === 'ATTENDANCE_SETTING_BELOW_MINIMUM' && error.data?.details?.minimum != null ? `Use at least ${error.data.details.minimum}.` : errorCopy[error?.code] || 'Attendance could not be updated. Please try again.';
export const uncertain = error => !error?.status || error.status >= 500;
// PIN bodies are deliberately never captured by a retry closure.
export function command(method, path, body, key = newKey()) {
  return frozenWrite(method, path, { ...body, idempotency_key: key }, { key });
}
export { newKey };
